import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet, View, ViewStyle, Pressable, Platform } from "react-native";
import Text from "../components/Text";
import { useTheme } from "../hooks/use-theme";
import { GestureDetector, Gesture } from "react-native-gesture-handler";
import { ShadowedView, shadowStyle } from "react-native-fast-shadow";
import * as ExpoHaptics from "expo-haptics";
import Animated, {
    Easing,
    ReduceMotion,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withSpring,
    withTiming,
} from "react-native-reanimated";

type SwitchProps = {
    value: boolean;
    onChange: (state: boolean) => void;
    disabled?: boolean;
    label?: string;
    labelPosition?: "left" | "right";
    testID?: string;
    style?: ViewStyle;
    thumbColor?: string;
    ios_backgroundColor?: string;
    trackColor?: { true: string; false: string };
};

const AnimatedShadowView = Animated.createAnimatedComponent(ShadowedView);

const SWITCH_HEIGHT = 31;
const SWITCH_WIDTH = 50;

const SWITCH_PADDING = 2;

const THUMB_SIZE = SWITCH_HEIGHT - SWITCH_PADDING * 2;

const EXTENDED_THUMB_SIZE = THUMB_SIZE * 1.25;
const THUMB_SIZE_DIFFERENCE = EXTENDED_THUMB_SIZE - THUMB_SIZE;

const DEFAULT_END_POSITION = SWITCH_WIDTH - THUMB_SIZE - SWITCH_PADDING * 2;

const ANIMATION_CONFIG = {
    duration: 850,
    dampingRatio: 0.75,
    stiffness: 400,
    overshootClamping: false,
    restDisplacementThreshold: 0.01,
    restSpeedThreshold: 0.01,
    reduceMotion: ReduceMotion.System,
};

const SHORT_ANIMATION_CONFIG = {
    duration: 200,
    easing: Easing.out(Easing.quad),
    reduceMotion: ReduceMotion.System,
};

const Switch = ({
    value,
    onChange,
    disabled,
    label,
    labelPosition = "right",
    testID,
    style,
    thumbColor,
    trackColor,
}: SwitchProps) => {
    const { colors } = useTheme();
    const styles = useStyles();

    const getTrackColor = useCallback(
        (currentValue: boolean) => {
            if (currentValue) {
                return trackColor?.true || (trackColor as unknown as string) || colors.secondary;
            } else {
                return trackColor?.false || colors.backdrop;
            }
        },
        [colors.backdrop, colors.secondary, trackColor],
    );

    const switchHasMoved = useSharedValue(false);
    const switchState = useSharedValue(value);

    const endPosition = useSharedValue(DEFAULT_END_POSITION);

    const trackAnimatedColor = useSharedValue(getTrackColor(value));

    const trackAnimatedStyles = useAnimatedStyle(() => ({
        backgroundColor: trackAnimatedColor.value,
    }));

    const thumbLeftPosition = useSharedValue(value ? DEFAULT_END_POSITION : 0);
    const thumbWidth = useSharedValue(THUMB_SIZE);

    const thumbAnimatedStyles = useAnimatedStyle(() => ({
        transform: [{ translateX: thumbLeftPosition.value }],
        width: thumbWidth.value,
    }));

    const vibrate = useCallback(() => {
        if (Platform.OS !== "web") {
            void ExpoHaptics.impactAsync(ExpoHaptics.ImpactFeedbackStyle.Light);
        }
    }, []);

    const onChangeValue = useCallback(
        (newValue: boolean, isExtended?: boolean) => {
            switchState.value = newValue;
            switchHasMoved.value = true;

            trackAnimatedColor.value = withTiming(getTrackColor(newValue), { duration: 300 });

            if (newValue) {
                thumbLeftPosition.value = withSpring(
                    DEFAULT_END_POSITION - (isExtended ? THUMB_SIZE_DIFFERENCE : 0),
                    ANIMATION_CONFIG,
                );
            } else {
                thumbLeftPosition.value = withSpring(0, ANIMATION_CONFIG);
            }
        },
        [getTrackColor, switchHasMoved, switchState, thumbLeftPosition, trackAnimatedColor],
    );

    const onPressIn = useCallback(() => {
        if (disabled) {
            return;
        }
        switchHasMoved.value = false;

        thumbWidth.value = withTiming(EXTENDED_THUMB_SIZE, SHORT_ANIMATION_CONFIG);
        endPosition.value = withTiming(
            DEFAULT_END_POSITION - THUMB_SIZE_DIFFERENCE,
            SHORT_ANIMATION_CONFIG,
        );
        if (switchState.value) {
            thumbLeftPosition.value = withTiming(
                thumbLeftPosition.value - THUMB_SIZE_DIFFERENCE,
                SHORT_ANIMATION_CONFIG,
            );
        }
    }, [disabled, endPosition, switchHasMoved, switchState, thumbLeftPosition, thumbWidth]);

    const onPressOut = useCallback(() => {
        if (disabled) {
            return;
        }
        if (!switchHasMoved.value && switchState.value !== value) return;

        let newSwitchState;

        if (!switchHasMoved.value) {
            newSwitchState = !switchState.value;
            vibrate();
        } else {
            newSwitchState = switchState.value;
        }
        const hasMoved = switchHasMoved.value;
        onChangeValue(newSwitchState);
        // In some rare cases, the onPressOut() event is triggered twice or more, with switchHasMoved.value === true.
        // In that case, don't call onChange again with the same value.
        if (!hasMoved) {
            onChange(newSwitchState);
        }

        thumbWidth.value = withTiming(THUMB_SIZE, SHORT_ANIMATION_CONFIG);

        switchState.value = newSwitchState;
    }, [
        disabled,
        onChange,
        onChangeValue,
        switchHasMoved,
        switchState,
        thumbWidth,
        value,
        vibrate,
    ]);

    useEffect(() => {
        if (value !== switchState.value) {
            onChangeValue(value);
        }
    }, [onChangeValue, switchState, value]);

    const thumbPan = useMemo(
        () =>
            Gesture.Simultaneous(
                Gesture.Tap()
                    .shouldCancelWhenOutside(false)
                    .maxDuration(10000)
                    .onTouchesUp(() => {
                        if (Platform.OS !== "web") {
                            runOnJS(onPressOut)();
                        }
                    })
                    .enabled(!disabled),
                Gesture.Pan()
                    .onUpdate((e) => {
                        if (!switchState.value) {
                            if (e.x > SWITCH_WIDTH / 2) {
                                runOnJS(vibrate)();
                                runOnJS(onChangeValue)(true, true);
                            }
                        } else {
                            if (e.x - EXTENDED_THUMB_SIZE < -(SWITCH_WIDTH / 2)) {
                                runOnJS(vibrate)();
                                runOnJS(onChangeValue)(false, true);
                            }
                        }
                    })
                    .enabled(!disabled),
            ),
        [disabled, onChangeValue, onPressOut, switchState, vibrate],
    );

    return (
        <View style={[styles.container, style, disabled ? styles.switchDisabled : null]}>
            {labelPosition === "left" && label && (
                <Text
                    style={styles.labelLeft}
                    onPress={() => {
                        if (!disabled) {
                            onChangeValue(!value);
                            onChange(!value);
                            vibrate();
                        }
                    }}
                >
                    {label}
                </Text>
            )}
            <Pressable onPress={onPressOut} onPressIn={onPressIn} testID={testID}>
                <Animated.View style={[styles.switch, trackAnimatedStyles]}>
                    <AnimatedShadowView
                        style={[
                            {
                                height: THUMB_SIZE,
                                width: THUMB_SIZE,
                                backgroundColor: thumbColor || colors.background,
                                borderRadius: THUMB_SIZE / 2,
                            },
                            shadowStyle({
                                opacity: 0.12,
                                radius: 6,
                                offset: [0, 2.5],
                            }),
                            thumbAnimatedStyles,
                        ]}
                    >
                        <GestureDetector gesture={thumbPan}>
                            <View style={styles.thumb} />
                        </GestureDetector>
                    </AnimatedShadowView>
                </Animated.View>
            </Pressable>
            {labelPosition === "right" && label && (
                <Text
                    style={styles.labelRight}
                    onPress={() => {
                        if (!disabled) {
                            onChangeValue(!value);
                            onChange(!value);
                            vibrate();
                        }
                    }}
                >
                    {label}
                </Text>
            )}
        </View>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "row",
                    alignItems: "center",
                    borderWidth: 0, // Keep this to avoid border cutting
                },
                switch: {
                    height: SWITCH_HEIGHT,
                    width: SWITCH_WIDTH,
                    borderRadius: SWITCH_HEIGHT / 2,
                    padding: SWITCH_PADDING,
                    overflow: "hidden",
                },
                thumb: {
                    flex: 1,
                },
                switchDisabled: {
                    opacity: 0.33,
                },
                labelLeft: {
                    flexGrow: 0,
                    flexShrink: 1,
                    marginRight: 10,
                },
                labelRight: {
                    flexGrow: 1,
                    flexShrink: 1,
                    marginLeft: 10,
                },
            }),
        [],
    );
};

export default Switch;
